import { defineStore } from 'pinia'
import type { City, CityStatus } from '~/types/city'
import { useCityStorage } from '~/composables/useCityStorage'
import {
  CITY_FROM_REDIRECT_COOKIE,
  buildGeoCatalogCityPath,
  buildVisitSectionPath,
  canAutoRedirectGeoCatalog,
  canAutoRedirectToVisitSection,
  clearGeoCatalogCityFilterCleared,
  extractGeoCatalogCitySlug,
  isGeoCatalogRootPath,
  isVisitSectionPath,
  normalizePathWithSlash,
  performGeoCatalogCityRedirect,
  performVisitSectionRedirect,
  replaceVisitCitySlug,
} from '~/utils/geoCatalog'

interface CityApiResponse {
  data: City | null
}

let cachedDevOutboundIpHint: string | null | undefined

async function getDevOutboundPublicIpHint(): Promise<string | undefined> {
  // Только для локальной разработки: под тестами import.meta.dev тоже true, и этот
  // внешний запрос к api.ipify.org висит до своего 4s-таймаута там, где внешняя сеть
  // закрыта (CI) — из-за чего mount-тесты упираются в 5s testTimeout. В тестах пропускаем
  // (import.meta.test от @nuxt/test-utils + NODE_ENV на случай, если флаг не подставлен).
  if (!import.meta.dev || import.meta.test || process.env.NODE_ENV === 'test') {
    return undefined
  }

  if (cachedDevOutboundIpHint !== undefined) {
    return cachedDevOutboundIpHint ?? undefined
  }

  try {
    const payload = await $fetch<{ ip?: string }>('https://api.ipify.org?format=json', {
      timeout: 4000,
    })
    const ip = payload.ip?.trim()
    cachedDevOutboundIpHint = ip && ip.length > 0 ? ip : null

    return cachedDevOutboundIpHint ?? undefined
  } catch {
    cachedDevOutboundIpHint = null

    return undefined
  }
}

function isGeoDependentPage(path: string): boolean {
  return extractGeoCatalogCitySlug(path) !== null
}

export const useCityStore = defineStore('cityStore', {
  state: () => ({
    current: null as City | null,
    status: 'pending' as CityStatus,
    showPopover: false,
    sidebarOpen: false,
    _initialized: false,
  }),

  getters: {
    cityName: (state) => state.current?.name ?? null,
    citySlug: (state) => state.current?.slug ?? null,
    prepositionalName: (state) => {
      if (!state.current) {
        return null
      }
      return state.current.name_cases?.prepositional_singular ?? state.current.name
    },
  },

  actions: {
    async hydrateFromRoute() {
      const route = useRoute()
      const urlSlug = extractGeoCatalogCitySlug(route.path)

      if (urlSlug) {
        await this.resolveBySlug(urlSlug)
        // Сегмент `/uslugi/{x}/` — либо город, либо узел каталога, и различает их только API.
        // Непроверенный слаг подставлять нельзя: страница услуги спрашивала «Вы в uzi?».
        // Порог `id !== 0` тот же, что в init().
        if (this.current && this.current.id !== 0) {
          if (this.status === 'pending' || this.status === 'loading') {
            this.status = 'suggested'
            this.showPopover = true
          }

          return
        }
      }

      if (this.status === 'pending') {
        this.status = 'unknown'
      }
    },

    async init() {
      if (this._initialized) {
        return
      }
      this._initialized = true

      const route = useRoute()
      const { getStoredCityData, getSuggestedCityData } = useCityStorage()

      const urlSlug = extractGeoCatalogCitySlug(route.path)

      const confirmed = getStoredCityData()
      const suggestedStored = confirmed ? null : getSuggestedCityData()

      if (confirmed) {
        this.current = { id: 0, name: confirmed.name, slug: confirmed.slug }
        this.status = 'chosen'
      } else if (suggestedStored) {
        this.current = {
          id: 0,
          name: suggestedStored.name,
          slug: suggestedStored.slug,
        }
        this.status = 'suggested'
        this.showPopover = true
      }

      if (urlSlug) {
        await this.resolveBySlug(urlSlug)
        if (this.current && this.current.id !== 0) {
          if (confirmed) {
            this.status = 'chosen'
            this.showPopover = false
            this.redirectToCatalogCityIfNeeded()
          } else {
            const { setSuggestedCity } = useCityStorage()
            this.status = 'suggested'
            this.showPopover = true
            setSuggestedCity(this.current.slug, this.current.name)
          }

          return
        }
      }

      if (confirmed) {
        await this.resolveBySlug(confirmed.slug)
        if (this.current && this.current.id !== 0) {
          this.status = 'chosen'
          this.showPopover = false
          this.redirectToCatalogCityIfNeeded()
          return
        }
      }

      // Заход из поиска по старому городскому адресу: 301 схлопнул путь в плоский узел, и город
      // остался только в куке от серверного редиректа — иначе выдача уезжает на всю страну.
      // Кука одноразовая: город дальше живёт в localStorage, как любой другой предложенный.
      if (!confirmed && !suggestedStored) {
        const fromRedirect = useCookie<string | null>(CITY_FROM_REDIRECT_COOKIE)
        const redirectedSlug = fromRedirect.value

        if (redirectedSlug) {
          fromRedirect.value = null
          await this.resolveBySlug(redirectedSlug)

          if (this.current && this.current.id !== 0) {
            const { setSuggestedCity } = useCityStorage()
            this.status = 'suggested'
            this.showPopover = true
            setSuggestedCity(this.current.slug, this.current.name)

            return
          }
        }
      }

      if (suggestedStored) {
        await this.resolveBySlug(suggestedStored.slug)
        if (!this.current || this.current.id === 0) {
          this.current = {
            id: 0,
            name: suggestedStored.name,
            slug: suggestedStored.slug,
          }
        }
        this.status = 'suggested'
        this.showPopover = true
        this.redirectToCatalogCityIfNeeded()

        return
      }

      if (this.status === 'chosen' || this.status === 'suggested') {
        this.redirectToCatalogCityIfNeeded()

        return
      }

      this.status = 'loading'
      await this.detectCity()
      this.redirectToCatalogCityIfNeeded()
    },

    redirectToCatalogCityIfNeeded(): void {
      if (!import.meta.client) {
        return
      }

      const route = useRoute()

      const slug = this.current?.slug
      if (!slug || this.status !== 'chosen') {
        return
      }

      if (canAutoRedirectToVisitSection(route.path, route.query)) {
        const targetPath = buildVisitSectionPath(slug)

        if (route.path.replace(/\/$/, '') === targetPath.replace(/\/$/, '')) {
          return
        }

        performVisitSectionRedirect(slug, route.query)

        return
      }

      if (!canAutoRedirectGeoCatalog(route.path, route.query)) {
        return
      }

      const pathWithSlash = normalizePathWithSlash(route.path)
      const targetPath = buildGeoCatalogCityPath(pathWithSlash, slug)

      if (route.path.replace(/\/$/, '') === targetPath.replace(/\/$/, '')) {
        return
      }

      performGeoCatalogCityRedirect(pathWithSlash, slug, route.query)
    },

    async resolveBySlug(slug: string) {
      const { apiQuery } = useApi()

      try {
        const allCities = await apiQuery<{ data: City[] }>(
          '/public/cities',
          'GET',
        )
        const city = allCities.data.find((c: City) => c.slug === slug)
        if (city) {
          this.current = city
        }
      } catch {
        // silent — will fall through to detection
      }
    },

    async syncFromLocalStorage() {
      if (!import.meta.client) {
        return
      }
      const { getStoredCityData } = useCityStorage()
      const confirmed = getStoredCityData()
      if (!confirmed) {
        return
      }

      this.current = { id: 0, name: confirmed.name, slug: confirmed.slug }
      this.status = 'chosen'
      this.showPopover = false
      await this.resolveBySlug(confirmed.slug)
      this.redirectToCatalogCityIfNeeded()
    },

    async detectCity(force = false) {
      if (!import.meta.client) {
        this.status = 'unknown'
        return
      }

      const { getConfirmedCitySlug, getStoredCityData } = useCityStorage()
      if (getConfirmedCitySlug()) {
        const data = getStoredCityData()
        if (data) {
          this.current = { id: 0, name: data.name, slug: data.slug }
          this.status = 'chosen'
          this.showPopover = false
        }
        await this.resolveBySlug(getConfirmedCitySlug()!)
        this.redirectToCatalogCityIfNeeded()
        return
      }

      if (!force && this.status === 'suggested' && this.current) {
        return
      }

      const hadSuggestedCity = this.status === 'suggested' && this.current
      if (!hadSuggestedCity) {
        this.status = 'loading'
      }

      const ipCity = await this.fetchByIp()
      if (ipCity) {
        this.setCitySuggested(ipCity)

        if (!force) {
          return
        }
      }

      // Браузерная геолокация запрашивается только по явному действию пользователя
      // (кнопка «Определить автоматически» в сайдбаре передаёт force=true),
      // чтобы не показывать permission prompt на pageload и не пессимизировать SEO.
      if (force && import.meta.client && 'geolocation' in navigator) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              maximumAge: 300000,
              timeout: 15000,
            })
          })

          const city = await this.fetchNearestByCoords(
            position.coords.latitude,
            position.coords.longitude,
          )

          if (city) {
            this.setCitySuggested(city)
            return
          }
        } catch {
          // geolocation denied or timed out
        }
      }

      if (this.status === 'loading') {
        this.status = hadSuggestedCity ? 'suggested' : 'unknown'
      }
    },

    async fetchNearestByCoords(lat: number, lon: number): Promise<City | null> {
      const { apiQuery } = useApi()

      try {
        const result = await apiQuery<CityApiResponse>(
          '/public/cities/nearest',
          'POST',
          { body: { lat, lon } },
        )
        return result.data
      } catch {
        return null
      }
    },

    async fetchByIp(): Promise<City | null> {
      const { apiQuery } = useApi()

      try {
        const hint = await getDevOutboundPublicIpHint()
        const result = await apiQuery<CityApiResponse>(
          '/public/cities/detect',
          'GET',
          hint ? { query: { hint_ip: hint } } : {},
        )

        return result.data
      } catch {
        return null
      }
    },

    setCitySuggested(city: City) {
      const { getConfirmedCitySlug, getStoredCityData, setSuggestedCity } = useCityStorage()
      if (getConfirmedCitySlug()) {
        const data = getStoredCityData()
        if (data) {
          this.current = { id: 0, name: data.name, slug: data.slug }
          this.status = 'chosen'
          this.showPopover = false
        }
        void this.resolveBySlug(getConfirmedCitySlug()!)
        return
      }

      this.current = city
      this.status = 'suggested'
      this.showPopover = true
      setSuggestedCity(city.slug, city.name)
      this.redirectToCatalogCityIfNeeded()
    },

    confirmCity() {
      const { setStoredCity } = useCityStorage()
      const route = useRoute()

      if (!this.current) {
        return
      }

      if (canAutoRedirectToVisitSection(route.path, route.query)) {
        setStoredCity(this.current.slug, this.current.name)
        performVisitSectionRedirect(this.current.slug, route.query)

        return
      }

      setStoredCity(this.current.slug, this.current.name)
      this.showPopover = false
      this.status = 'chosen'
      this.redirectToCatalogCityIfNeeded()
    },

    selectCity(city: City) {
      const { setStoredCity } = useCityStorage()
      const route = useRoute()
      const router = useRouter()

      const oldSlug = this.current?.slug
      setStoredCity(city.slug, city.name)
      clearGeoCatalogCityFilterCleared()

      if (canAutoRedirectToVisitSection(route.path, route.query)) {
        performVisitSectionRedirect(city.slug, route.query)

        return
      }

      this.current = city
      this.status = 'chosen'
      this.showPopover = false
      this.sidebarOpen = false

      if (isVisitSectionPath(route.path)) {
        const newPath = replaceVisitCitySlug(route.path, city.slug)
        if (newPath !== route.path) {
          router.push({ path: newPath, query: route.query })
        }
        return
      }

      if (isGeoDependentPage(route.path) && oldSlug) {
        const newPath = route.path.replace(oldSlug, city.slug)
        if (newPath !== route.path) {
          const {district, metro, ...geoNeutralQuery} = route.query
          router.push({path: newPath, query: geoNeutralQuery})
        }
        return
      }

      if (isGeoCatalogRootPath(route.path)) {
        this.redirectToCatalogCityIfNeeded()
      }
    },

    dismissPopover() {
      if (this.status === 'suggested') {
        return
      }

      this.showPopover = false
    },

    openSidebar() {
      this.sidebarOpen = true
    },

    closeSidebar() {
      this.sidebarOpen = false
    },

    resetCity() {
      const { clearStoredCity } = useCityStorage()

      clearStoredCity()
      this.current = null
      this.status = 'pending'
      this.showPopover = false
      this._initialized = false
    },
  },
})
